import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OAuthService } from 'angular-oauth2-oidc';
import { SessionMonitorService } from './session-monitor.service';
import { HeaderDataStore } from '@v1/features/enterprises/pages/layout/store/header-data.store';
import { CentrifugoConnectionService } from '@v1/core/centrifugo/centrifugo-connection.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly sessionMonitor = inject(SessionMonitorService);
  private readonly headerDataStore = inject(HeaderDataStore);
  private readonly centrifugoConnection = inject(CentrifugoConnectionService);

  private readonly tokenRefreshEvent = toSignal(this.oauthService.events, {
    initialValue: null,
  });

  constructor() {
    effect(() => {
      const event = this.tokenRefreshEvent();
      if (event?.type === 'token_refreshed' || event?.type === 'silently_refreshed') {
        this.headerDataStore.setAccessToken(this.oauthService.getAccessToken());
      }
    });
  }

  public async handleCallback(): Promise<boolean> {
    if (!this.oauthService.discoveryDocumentLoaded) {
      await this.oauthService.loadDiscoveryDocument();
    }

    await this.oauthService.tryLoginCodeFlow();
    const valid = this.oauthService.hasValidAccessToken();
    if (valid) this.sessionMonitor.start();
    return valid;
  }

  public login(additionalState?: string): void {
    this.oauthService.initCodeFlow(additionalState);
  }

  public setupAutomaticSilentRefresh() {
    this.oauthService.setupAutomaticSilentRefresh();
  }

  public async logout(): Promise<boolean> {
    const loggedOut = await this.sessionMonitor.logout();
    if (loggedOut) this.centrifugoConnection.disconnect();
    return loggedOut;
  }
}
